import { useMemo } from "react";
import Chart, { useChart } from "src/components/chart";
import { Skeleton, Stack, Typography, Tooltip, TooltipProps, tooltipClasses, styled } from "@mui/material";
import { useVisibilityQuery } from "src/context/api/non-branded/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { fDate } from "src/utils/format-time";
import { fPercent } from "src/utils/format-number";
import { addWeeks, startOfWeek } from "date-fns";
import { useNonBrandedContext } from "./context/use-non-branded";
import { useLocales } from "src/locales";
import DiffIcon from "./diff-icon";
import { palette } from "./figma-palette";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));


export default function VisibilityChart() {
    const workspace = useCurrentWorkspace();
    const filterStore = useSelector((state: RootState) => state.filters);
    const { t, currentLang: { value: lang } } = useLocales();

    const { data: visibilityData, isLoading, isFetching, isUninitialized } = useVisibilityQuery({
        workspace_id: workspace?.id as number,
        start_date: fDate(filterStore.start, "yyyy-MM-dd"),
        end_date: fDate(filterStore.end, "yyyy-MM-dd"),
        devices: filterStore.devices,
        countries: filterStore.countries,
        platforms: filterStore.platforms,
        interval: "year_week",
        search_terms: filterStore.keywords,
        compare: filterStore.compare,
        compare_start_date: fDate(filterStore.compareStart, "yyyy-MM-dd"),
        compare_end_date: fDate(filterStore.compareEnd, "yyyy-MM-dd"),
    }, { skip: !workspace?.id || filterStore.countries.length === 0 });

    /** Nombre de semaines affichées (aligné avec l’historique ~12 semaines de la requête). */
    const CHART_WEEKS = 12;

    const { weeks: visibilityWeeks, categories } = useMemo(() => {
        const rows = visibilityData?.base?.data ?? [];

        const weekKeyFromApi = (date_interval: string): string => {
            const ymd = date_interval.slice(0, 10);
            if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
                return fDate(startOfWeek(new Date(`${ymd}T12:00:00`), { weekStartsOn: 1 }), "yyyy-MM-dd");
            }
            const parsed = new Date(date_interval);
            return fDate(startOfWeek(parsed, { weekStartsOn: 1 }), "yyyy-MM-dd");
        };

        const byWeek = new Map<string, (typeof rows)[number]>();
        rows.forEach((row) => {
            if (!row.date_interval) {
                return;
            }
            byWeek.set(weekKeyFromApi(row.date_interval), row);
        });

        // Ancrer les 12 semaines sur la fin de période filtrée (aligné avec l’API), pas sur « aujourd’hui ».
        const endYmd = fDate(filterStore.end, "yyyy-MM-dd");
        const endMonday = startOfWeek(new Date(`${endYmd}T12:00:00`), { weekStartsOn: 1 });
        const weeks = Array.from({ length: CHART_WEEKS }, (_, i) => {
            const monday = addWeeks(endMonday, i - (CHART_WEEKS - 1));
            const key = fDate(monday, "yyyy-MM-dd");
            const row = byWeek.get(key);
            const total_visibility_rate = row?.total_visibility_rate ?? 0;
            const date_interval = row?.date_interval ?? key;
            const label = monday.toLocaleDateString(lang, {
                day: "numeric",
                month: "short",
                year: "numeric",
            });
            return { key, date_interval, total_visibility_rate, label };
        });

        return {
            weeks,
            categories: weeks.map((w) => w.label),
        };
    }, [visibilityData, lang, filterStore.end]);

    const chartOptions = useChart({
        chart: {
            type: 'area',
            toolbar: { show: false },
        },
        stroke: {
            width: 2,
            curve: 'smooth',
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.4,
                opacityTo: 0.1,
                stops: [0, 100],
            },
        },
        xaxis: {
            type: 'category',
            tooltip: {
                enabled: false,
            },
            categories,
            labels: {
                show: false,
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            tooltip: {
                enabled: false,
            },
            labels: {
                show: false,
            },
        },
        grid: {
            show: false,
        },
        tooltip: {
            shared: true,
            intersect: false,
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const item = visibilityWeeks[dataPointIndex] ?? {
                    total_visibility_rate: 0,
                    date_interval: "",
                };

                const visibilityRate = item.total_visibility_rate || 0;

                let date = '';
                if (item.date_interval) {
                    try {
                        const dateObj = new Date(item.date_interval);
                        if (!isNaN(dateObj.getTime())) {
                            date = dateObj.toLocaleDateString(lang, {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                            });
                        } else {
                            date = item.date_interval;
                        }
                    } catch {
                        date = item.date_interval;
                    }
                }

                return `
                    <div class="visibility-chart-tooltip">
                        <div class="visibility-chart-tooltip-header">
                            <span class="visibility-chart-tooltip-title">${t("dashboard.nonBrand.columns.visibility")}</span>
                            <span class="visibility-chart-tooltip-date">${date}</span>
                        </div>
                        <div class="visibility-chart-tooltip-row">
                            <div class="visibility-chart-tooltip-indicator"></div>
                            <span class="visibility-chart-tooltip-label">${t("dashboard.nonBrand.columns.visibility")}</span>
                            <span class="visibility-chart-tooltip-value">${fPercent(visibilityRate)}</span>
                        </div>
                    </div>
                `;
            },
        },
        legend: {
            show: false,
        },
        colors: [palette.visibility],
    });

    const series = [
        {
            name: 'Visibility',
            data: visibilityWeeks.map((w) => w.total_visibility_rate),
        },
    ];

    const diff = visibilityData?.diff?.total_visibility_rate || 0;
    const evolution = visibilityData?.evolution?.total_visibility_rate || 0;


    return (
        <>
            <Stack className="card-top-header" borderBottom={1} borderColor="divider" direction="row" spacing={3} py={1} px={2} justifyContent="space-between">
                {/* <Stack className="card-top-header-item" direction="row" alignItems="center" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <line x1="1" y1="7" x2="13" y2="7" stroke="#1EC5D0" stroke-width="2" stroke-linecap="round" />
                        </svg>
                        <Typography className="card-top-header-item-label" variant="h6" color="text.primary">
                            Search coverage
                        </Typography>
                    </Stack>
                    <Typography className="card-top-header-item-value" variant="h6" color="text.primary">
                        0
                    </Typography>
                </Stack> 
                */}
                <LightTooltip title={t("dashboard.nonBrand.tooltips.card.visibilityRate")}>
                    <Stack className="card-top-header-item" direction="row" alignItems="center" spacing={1} sx={{ cursor: 'help' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <svg className="card-top-header-item-indicator" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <line x1="1" y1="7" x2="13" y2="7" stroke={palette.visibility} stroke-width="2" stroke-linecap="round" />
                            </svg>
                            <Typography className="card-top-header-item-label" variant="h6" color="text.primary">
                                {t("dashboard.nonBrand.titles.visibility")}
                            </Typography>
                        </Stack>
                        <Typography className="card-top-header-item-value" variant="h6" color="text.primary">
                            {fPercent(visibilityData?.base?.total_visibility_rate || 0)}
                        </Typography>
                        {diff !== 0 && (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <DiffIcon plus={diff > 0} minus={diff < 0} good={diff > 0} bad={diff < 0} evolution={evolution} />
                            </Stack>
                        )}
                    </Stack>
                </LightTooltip>
            </Stack>
            {
                isUninitialized || isLoading || isFetching ? (
                    <Skeleton variant="rectangular" width="100%" height={"100%"} />
                )
                    :
                    (
                        <Stack width={"inherit"} flex={1} height={149}>
                            <Chart
                                type="area"
                                series={series}
                                options={chartOptions}
                                height="100%"
                            />
                        </Stack>
                    )
            }
        </>
    )
}
